/**
 * ルール起点の FAQ 生成。
 *
 * 商品詳細からの起動（既定の指示で 1 商品ぶん）と同じ約束に従う:
 * - 見積もりを見ずに起動できる経路を作らない。開始に渡す対象件数は、見積もりが
 *   返した `targetCount` を**そのまま**渡す（画面が数え直さない）
 * - 進捗は台帳から引き直す。起動の応答に入っていた状態を持ち続けない
 * - 間隔は `Job.suggestedPollIntervalSec` に従う
 *
 * 違うのは範囲で、こちらは**母数ぶん全部**に対して走る。配信中の版が無ければ
 * API が断る（どの版で作ったか辿れない答えを残さないため）ので、画面も先に断る。
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "urql";
import {
  Button,
  Card,
  ErrorState,
  FaqGenerationEstimateDocument,
  LoadingState,
  Meter,
  RuleJobProgressDocument,
  StartFaqGenerationDocument,
  Tag,
  TenantRunningJobsDocument,
  type JobEstimateFieldsFragment,
  type JobState,
  type RuleDetailFieldsFragment,
} from "visumo-data-agent-web-shared";

import { HelpIcon } from "../help/HelpIcon";

import {
  DEFAULT_FAQ_GENERATION_POPULATION,
  POPULATION_LABEL,
  TENANT_WIDE_JOB_NOTE,
} from "../api-capabilities";
import { DroppedReasons, RuleErrors, type RuleUserError } from "./RuleParts";

const TERMINAL_STATES: readonly JobState[] = [
  "SUCCEEDED",
  "SUCCEEDED_WITH_ERRORS",
  "FAILED",
  "CANCELLED",
];

function isTerminal(state: JobState): boolean {
  return TERMINAL_STATES.includes(state);
}

function count(value: number | null | undefined): string {
  return value == null ? "—" : value.toLocaleString("ja-JP");
}

export function RuleFaqGenerationPanel({
  rule,
}: {
  rule: RuleDetailFieldsFragment;
}) {
  const [confirming, setConfirming] = useState(false);
  const [jobId, setJobId] = useState<string | null>(null);
  const [pickedUp, setPickedUp] = useState(false);
  const [pollTick, setPollTick] = useState(0);
  const [userErrors, setUserErrors] = useState<RuleUserError[]>([]);
  // 1 起動につき 1 つ。再送（CONFLICT からのやり直し）では作り直さない
  const idempotencyKey = useRef<string | null>(null);

  const target = { population: DEFAULT_FAQ_GENERATION_POPULATION };
  const published = rule.publishedPromptVersion;

  const [estimateResult, refetchEstimate] = useQuery({
    query: FaqGenerationEstimateDocument,
    variables: { input: target },
    pause: !confirming,
    requestPolicy: "network-only" as const,
  });

  const [runningResult] = useQuery({
    query: TenantRunningJobsDocument,
    variables: {
      kind: "FAQ_GENERATION" as const,
      state: "RUNNING" as JobState,
    },
    pause: jobId !== null,
  });
  const [queuedResult] = useQuery({
    query: TenantRunningJobsDocument,
    variables: { kind: "FAQ_GENERATION" as const, state: "QUEUED" as JobState },
    pause: jobId !== null,
  });

  const [jobResult, refetchJob] = useQuery({
    query: RuleJobProgressDocument,
    variables: { id: jobId ?? "" },
    pause: jobId === null,
    requestPolicy: "network-only" as const,
  });

  const [startState, startFaqGeneration] = useMutation(
    StartFaqGenerationDocument,
  );

  const job = jobResult.data?.job ?? null;

  useEffect(() => {
    if (jobId !== null) {
      return;
    }
    const found =
      runningResult.data?.jobs.edges[0]?.node ??
      queuedResult.data?.jobs.edges[0]?.node ??
      null;
    if (found !== null) {
      setJobId(found.id);
      setPickedUp(true);
      setConfirming(false);
    }
  }, [jobId, runningResult.data, queuedResult.data]);

  useEffect(() => {
    if (jobId === null || job === null) {
      return;
    }
    const intervalSec = job.suggestedPollIntervalSec;
    if (intervalSec == null || intervalSec <= 0) {
      return;
    }
    const timer = setTimeout(() => {
      setPollTick((tick) => tick + 1);
      refetchJob({ requestPolicy: "network-only" });
    }, intervalSec * 1000);
    return () => clearTimeout(timer);
  }, [jobId, job, pollTick, refetchJob]);

  const start = useCallback(
    (estimate: JobEstimateFieldsFragment) => {
      idempotencyKey.current ??= crypto.randomUUID();
      void startFaqGeneration({
        input: {
          target,
          ruleId: rule.id,
          // 画面が数え直さず、見積もりが返した件数をそのまま渡す
          acknowledgedTargetCount: estimate.targetCount,
          idempotencyKey: idempotencyKey.current,
        },
      }).then((mutationResult) => {
        if (mutationResult.error !== undefined) {
          setUserErrors([
            {
              field: null,
              code: "INTERNAL_ERROR",
              message: `生成を始められませんでした: ${mutationResult.error.message}`,
            },
          ]);
          return;
        }
        const payload = mutationResult.data?.startFaqGeneration;
        if (payload === undefined) {
          return;
        }
        if (payload.userErrors.length > 0) {
          setUserErrors([...payload.userErrors]);
          return;
        }
        setUserErrors([]);
        const started = payload.job ?? null;
        if (started !== null) {
          setJobId(started.id);
          setPickedUp(false);
          setConfirming(false);
          idempotencyKey.current = null;
        }
      });
    },
    [rule.id, startFaqGeneration],
  );

  const estimate = estimateResult.data?.faqGenerationEstimate ?? null;

  return (
    <Card
      title="このルールで答えを作る"
      note="作った答えは、このルールが配信中（RUN）のあいだだけ配信されます。押すとまず見積もりを出します。"
      extra={
        <span className="vd-row">
          {jobId === null && !confirming && published !== null ? (
            <Button variant="primary" onClick={() => setConfirming(true)}>
              このルールで生成する
            </Button>
          ) : null}
          <HelpIcon slug="rules/faq-generation" />
        </span>
      }
    >
      <div className="vd-stack">
        <RuleErrors title="生成を始められませんでした" errors={userErrors} />

        {published === null ? (
          <p style={{ margin: 0 }}>
            配信中の版がまだありません。先に版を保存して、配信する版に決めてください。
          </p>
        ) : jobId !== null ? (
          jobResult.error !== undefined ? (
            <ErrorState
              title="進み具合を読み込めませんでした"
              description={jobResult.error.message}
              onRetry={() => refetchJob({ requestPolicy: "network-only" })}
            />
          ) : job === null ? (
            <LoadingState label="進み具合を読み込んでいます" />
          ) : (
            <>
              {pickedUp ? (
                <p className="vd-stat-note" style={{ margin: 0 }}>
                  {TENANT_WIDE_JOB_NOTE}
                </p>
              ) : null}
              {isTerminal(job.state) ? (
                <div className="vd-job" role="status">
                  <div className="vd-card-title">
                    生成が終わりました
                    <Tag tone={job.state === "FAILED" ? "danger" : "ok"}>
                      {job.state === "FAILED" ? "失敗" : "完了"}
                    </Tag>
                  </div>
                  <div className="vd-grid">
                    <div>
                      <div className="vd-stat-label">作れた</div>
                      <div className="vd-stat-value">
                        {count(job.succeeded)} 件
                      </div>
                    </div>
                    <div>
                      <div className="vd-stat-label">作れなかった</div>
                      <div className="vd-stat-value">
                        {count(job.failed)} 件
                      </div>
                    </div>
                    <div>
                      <div className="vd-stat-label">対象外</div>
                      <div className="vd-stat-value">
                        {count(job.skipped)} 件
                      </div>
                    </div>
                    <div>
                      <div className="vd-stat-label">次回に回った</div>
                      <div className="vd-stat-value">
                        {count(job.deferred)} 件
                      </div>
                    </div>
                  </div>
                  {job.errorSummary === null ? null : (
                    <p style={{ margin: "4px 0 0" }}>{job.errorSummary}</p>
                  )}
                  <DroppedReasons
                    droppedFaqs={job.droppedFaqs}
                    reasons={job.droppedFaqReasons}
                  />
                  <div className="vd-row">
                    <Button
                      onClick={() => {
                        setJobId(null);
                        setPickedUp(false);
                      }}
                    >
                      閉じる
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="vd-job" role="status">
                  <div className="vd-card-title">
                    答えを作っています（{count(job.succeeded)} /{" "}
                    {count(job.total)}）<Tag tone="info">実行中</Tag>
                  </div>
                  <Meter ratio={job.progress} />
                  <p className="vd-stat-note" style={{ marginBottom: 0 }}>
                    この画面を閉じても進みます。
                  </p>
                </div>
              )}
            </>
          )
        ) : !confirming ? (
          <p style={{ margin: 0 }}>
            {POPULATION_LABEL[DEFAULT_FAQ_GENERATION_POPULATION]}
            に対して、配信中の版（{published}）で答えを作ります。
          </p>
        ) : estimateResult.error !== undefined ? (
          <ErrorState
            title="見積もりを出せませんでした"
            description={estimateResult.error.message}
            onRetry={() => refetchEstimate({ requestPolicy: "network-only" })}
          />
        ) : estimate === null ? (
          <LoadingState label="見積もりを出しています" />
        ) : (
          <>
            <div className="vd-grid">
              <div>
                <div className="vd-stat-label">生成する商品</div>
                <div className="vd-stat-value">
                  {count(estimate.targetCount)} 件
                </div>
                <div className="vd-stat-note">{estimate.basis.label}</div>
              </div>
              <div>
                <div className="vd-stat-label">AI への問い合わせ</div>
                <div className="vd-stat-value">
                  {count(estimate.estimatedLlmCalls)} 回
                </div>
                <div className="vd-stat-note">予算はこの回数で数えています</div>
              </div>
              <div>
                <div className="vd-stat-label">費用の見込み</div>
                <div className="vd-stat-value">
                  {estimate.estimatedCost == null
                    ? "出せません"
                    : `${estimate.estimatedCost.toLocaleString("ja-JP")} ${estimate.estimatedCurrency ?? ""}`.trim()}
                </div>
                <div className="vd-stat-note">
                  {estimate.estimatedCost == null
                    ? (estimate.costGap?.why ?? "単価の記録がありません")
                    : "実行後の請求で確定します"}
                </div>
              </div>
            </div>
            <div className="vd-row">
              <Button
                variant="primary"
                disabled={startState.fetching}
                onClick={() => start(estimate)}
              >
                {startState.fetching ? "起動しています" : "この内容で生成する"}
              </Button>
              <Button
                disabled={startState.fetching}
                onClick={() => setConfirming(false)}
              >
                やめる
              </Button>
            </div>
          </>
        )}
      </div>
    </Card>
  );
}
